/**
 * Locale configuration — app-facing barrel.
 *
 * Single source of truth for locale-related configuration. Re-exports the
 * pure/build-safe subset from `./locale-config-core.js` (the piece destined for
 * @scrawlr/lib-js-i18n) and adds the app-specific data that stays host-side:
 * select-option labels, the host-default policy (reads `window`), and the
 * Stripe Connect locale mapping. Import paths for all consumers are unchanged.
 */

// DEFAULT_LOCALE is used locally by stripeConnectLocale below; the full
// pure/build-safe subset is re-exported for consumers at the bottom of this file.
import { DEFAULT_LOCALE } from './locale-config-core.js'

/**
 * Locale select options (app-specific display labels). Codes must stay in sync
 * with `SUPPORTED_LOCALE_CODES` in `./locale-config-core.js` (enforced by
 * tests/unit/locale-config-contract.test.js).
 */
export const LOCALE_SELECT_OPTIONS = [
    {
        code: 'en', label: 'English',
    },
    {
        code: 'fr-ca', label: 'Français', sublabel: 'French (Canada)',
    },
    {
        code: 'es-mx', label: 'Español', sublabel: 'Spanish (Mexico)',
    },
]

/**
 * Find the locale select option object for a given locale code
 * @param {string} code - Locale code (e.g. 'en', 'fr-ca')
 * @returns {{ code: string, label: string, sublabel?: string } | undefined}
 */
export function getLocaleSelectOption (code) {
    return LOCALE_SELECT_OPTIONS.find((option) => option.code === code)
}

/**
 * Whether a hostname is a lodgr.mx host (apex or any subdomain).
 *
 * @param {string} host - Lower-cased hostname
 * @returns {boolean}
 */
export function isMexicanHost (host) {
    return host === 'lodgr.mx' || host.endsWith('.lodgr.mx')
}

/**
 * Whether the current visit is on lodgr.mx — the real hostname, or
 * `?simulateHost=lodgr.mx` for local testing without /etc/hosts.
 *
 * Shared by every host-implied default (locale here, currency in
 * `currency-config.js`) so they agree on what "a lodgr.mx visit" is, while each
 * default stays independent of the other.
 *
 * @param {{ hostname: string, search?: string }} location
 * @returns {boolean}
 */
export function isMexicanHostVisit ({
    hostname,
    search,
}) {
    if (typeof hostname === 'string' && isMexicanHost(hostname.toLowerCase())) {
        return true
    }

    const simulateHost = new URLSearchParams(search || '')
        .get('simulateHost')
        ?.toLowerCase()
    return Boolean(simulateHost && isMexicanHost(simulateHost))
}

/**
 * Locale implied by the current hostname when no saved preference exists.
 * e.g. lodgr.mx → es-mx. Not written to localStorage.
 *
 * Local testing without /etc/hosts: `?simulateHost=lodgr.mx` on any host.
 *
 * @param {string} [search] - Optional query string (e.g. `location.search`);
 *   defaults to `window.location.search`.
 * @returns {string | null}
 */
export function getHostDefaultLocale (search = window.location.search) {
    if (isMexicanHostVisit({
        hostname: window.location.hostname,
        search,
    })) {
        return 'es-mx'
    }

    // eslint-disable-next-line unicorn/no-null -- null means "no host-implied locale" (distinct from a resolved code)
    return null
}

/** Stripe Connect `locale` (BCP-47) per URL path segment — keep keys aligned with {@link SUPPORTED_LOCALES}. */
const STRIPE_CONNECT_LOCALE_BY_SEGMENT = {
    en: 'en-US',
    'es-mx': 'es-MX',
    'fr-ca': 'fr-CA',
}

/**
 * BCP-47 locale for `loadConnectAndInitialize` from the active path locale segment.
 *
 * @see https://docs.stripe.com/connect/get-started-connect-embedded-components#localization
 * @param {string} [pathLocaleSegment]
 * @returns {string}
 */
export function stripeConnectLocale (pathLocaleSegment = DEFAULT_LOCALE) {
    const segment = typeof pathLocaleSegment === 'string' ? pathLocaleSegment : DEFAULT_LOCALE

    return STRIPE_CONNECT_LOCALE_BY_SEGMENT[segment] ?? STRIPE_CONNECT_LOCALE_BY_SEGMENT[DEFAULT_LOCALE]
}

/**
 * Google Maps JavaScript API `language` per URL path segment — keep keys aligned with
 * {@link SUPPORTED_LOCALES}. Spanish maps to `es-419` (Latin American Spanish), the closest code on
 * Google's supported-language list; it does not list `es-MX`.
 */
const GOOGLE_MAPS_LANGUAGE_BY_SEGMENT = {
    en: 'en',
    'es-mx': 'es-419',
    'fr-ca': 'fr-CA',
}

/**
 * `language` query parameter for the Maps JavaScript API bootstrap URL, from the active path
 * locale segment. Without it Google picks the browser language, so the map ignored the site locale.
 *
 * @see https://developers.google.com/maps/documentation/javascript/localization
 * @param {string} [pathLocaleSegment]
 * @returns {string}
 */
export function googleMapsLanguage (pathLocaleSegment = DEFAULT_LOCALE) {
    const segment = typeof pathLocaleSegment === 'string' ? pathLocaleSegment : DEFAULT_LOCALE

    return GOOGLE_MAPS_LANGUAGE_BY_SEGMENT[segment] ?? GOOGLE_MAPS_LANGUAGE_BY_SEGMENT[DEFAULT_LOCALE]
}

/**
 * Stripe.js Elements `locale` per URL path segment. A SEPARATE table from the
 * Connect one above because the two Stripe products accept different tags:
 * Elements lists `fr-CA` and `es-419` (Latin American Spanish) but has no
 * `es-MX` — https://docs.stripe.com/js/appendix/supported_locales. Keep keys
 * aligned with {@link SUPPORTED_LOCALES}.
 */
const STRIPE_ELEMENTS_LOCALE_BY_SEGMENT = {
    en: 'en',
    'es-mx': 'es-419',
    'fr-ca': 'fr-CA',
}

/**
 * Locale for `stripe.elements({ locale })` from the active path locale segment,
 * so the checkout card form follows the page language rather than the
 * browser's (TENANT-5532 C5). Unknown segments fall back to English.
 *
 * @param {string} [pathLocaleSegment]
 * @returns {string}
 */
export function stripeElementsLocale (pathLocaleSegment = DEFAULT_LOCALE) {
    const segment = typeof pathLocaleSegment === 'string' ? pathLocaleSegment : DEFAULT_LOCALE

    return STRIPE_ELEMENTS_LOCALE_BY_SEGMENT[segment] ?? STRIPE_ELEMENTS_LOCALE_BY_SEGMENT[DEFAULT_LOCALE]
}

// Re-export the pure/build-safe subset unchanged so existing consumers keep
// importing these from './locale-config.js'.
export {
    DEFAULT_LOCALE,
    isExternalPath,
    isLocaleSegment,
    isProtocolPath,
    normalizeLocale,
    SUPPORTED_LOCALE_CODES,
    SUPPORTED_LOCALES,
} from './locale-config-core.js'
